import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import RevisionForm, {
  RevisionFormData,
  RevisionDevice,
  revisionFormInitialValues,
} from 'components/forms/revision/RevisionForm';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FORMS_PAGE_PATH, REVISIONS_PAGE_PATH } from 'routes/paths';
import { ERROR_NOTIFICATION_COLOR, SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import { DeviceGetResponse } from 'api/actions/device-get/device-get-response';

/**
 * Type for initial devices from search params
 */
type InitialDevice = { deviceId: number; termId?: number };

/**
 * Page used to create a new revision.
 *
 */
export default function AddRevisionPage({ kind }: { kind: 'revision' | 'form' }) {
  const navigate = useNavigate();
  const { getAction } = useApi();

  const [searchParams] = useSearchParams();
  const [initialValues, setInitialValues] = useState<RevisionFormData | null>(null);

  // Initialize form values with devices data
  useEffect(() => {
    /** Parse initial devices from search params */
    const parseInitialDevices = (): InitialDevice[] => {
      const devicesParam = searchParams.get('devices');
      if (!devicesParam) {
        return [];
      }

      try {
        const parsed = JSON.parse(decodeURIComponent(devicesParam));
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };

    /** Transform device response into form device structure */
    const transformDeviceData = (device: DeviceGetResponse, termId?: number): RevisionDevice => ({
      ...device,
      termId: termId ? String(termId) : '',
      checked: true,
      type: device.deviceSubtype?.deviceTypeName ?? '',
      deviceTypeId: device.deviceType.deviceTypeId,
      deviceSubtypeId: device.deviceSubtype?.deviceTypeId ?? 0,
      deviceSubtypeName: device.deviceSubtype?.deviceTypeName ?? '',
      revisionPlan: (device.nextRevision || []).map((plan) => ({
        revisionPlanId: plan.revisionPlanId!,
        description: plan.description ?? '',
        date: plan.date ?? '',
        revisionPeriod: plan.revisionPeriod ?? 1,
        neverExecuted: plan.neverExecuted ?? false,
        deviceTypeId: plan.deviceTypeId ?? device.deviceType.deviceTypeId,
      })),
    });

    /** Async function to initialize form values with fetched device data */
    const initializeValues = async () => {
      try {
        const initialDevices = parseInitialDevices();
        const deviceGetAction = getAction('DeviceGet');

        // Fetch device data for all initial devices
        const deviceDataPromises = initialDevices.map((device) =>
          deviceGetAction({ parameters: { deviceId: String(device.deviceId) } }).then((response) => ({
            deviceId: device.deviceId,
            termId: device.termId,
            data: response,
          }))
        );

        const deviceDataResults = await Promise.all(deviceDataPromises);

        const parentInitialDevice = deviceDataResults.find(({ data }) => data.structure === 'parent');
        const normalizedInitialDevices = parentInitialDevice ? [parentInitialDevice] : deviceDataResults;

        // Transform device data into form structure
        const devices: RevisionFormData['devices'] = {};
        normalizedInitialDevices.forEach(({ deviceId, termId, data }) => {
          devices[String(deviceId)] = transformDeviceData(data, termId);
        });

        if (parentInitialDevice?.data.childDevices?.length) {
          const childDeviceIds = parentInitialDevice.data.childDevices
            .filter(({ deviceType }) => deviceType.deviceTypeId === parentInitialDevice.data.deviceType.deviceTypeId)
            .map((childDevice) => childDevice.deviceId);

          const childDeviceDataResults = await Promise.all(
            childDeviceIds.map((childDeviceId) =>
              deviceGetAction({ parameters: { deviceId: String(childDeviceId) } }).then((response) => ({
                deviceId: childDeviceId,
                data: response,
              }))
            )
          );

          childDeviceDataResults.forEach(({ deviceId, data }) => {
            devices[String(deviceId)] = transformDeviceData(data);
          });
        }

        // Set initial values with fetched device data
        setInitialValues({
          ...revisionFormInitialValues,
          subKind: kind === 'form' ? '' : 'revision',
          revisionName: searchParams.get('revisionName') ?? '',
          deadline: searchParams.get('deadline') ?? '',
          organizationId: searchParams.get('organizationId') ?? '',
          departmentId: searchParams.get('departmentId') ?? '',
          deviceTypeId: searchParams.get('deviceTypeId') ?? '',
          technicianId: searchParams.get('technicianId') ?? '',
          devices,
        });
      } catch (error) {
        panic(error as Error);
      }
    };

    initializeValues();
  }, [searchParams, getAction]);

  /** Runs when the form is submitted. */
  const onSubmit = async ({
    subKind,
    revisionName,
    deadline,
    organizationId,
    departmentId,
    deviceTypeId,
    devices,
    technicianId,
  }: RevisionFormData) => {
    try {
      if (kind !== 'revision' && !subKind) {
        notifications.show({
          title: 'Chyba',
          message: 'Vyberte typ formuláru.',
          color: ERROR_NOTIFICATION_COLOR,
        });

        return;
      }

      const revisionCreateAction = getAction('RevisionCreate');

      const mappedDevices = Object.values(devices)
        .filter(({ checked }) => checked)
        .map((device) => ({
          deviceId: device.deviceId,
          deviceSubtypeId: device.deviceSubtypeId,
          termId: device.termId ? Number(device.termId) : undefined,
        }));

      await revisionCreateAction({
        payload: {
          revisionName,
          deadline: deadline || undefined,
          organizationId: Number(organizationId),
          departmentId: Number(departmentId),
          deviceTypeId: Number(deviceTypeId),
          technicianId: technicianId ? Number(technicianId) : undefined,
          kind: kind === 'revision' ? 'revision' : subKind!,
          devices: mappedDevices,
        },
      });

      notifications.show({
        title: kind === 'revision' ? 'Revízna správa bola vytvorená' : 'Formulár bol vytvorený',
        message:
          kind === 'revision'
            ? `Revízna správa ${revisionName} bola úspešne vytvorená.`
            : `Formulár ${revisionName} bol úspešne vytvorený.`,
        color: SUCCESS_NOTIFICATION_COLOR,
      });

      navigate(kind === 'revision' ? REVISIONS_PAGE_PATH.original : FORMS_PAGE_PATH.original);
    } catch (error) {
      return panic(error as Error);
    }
  };

  return (
    <DashboardLayout
      breadcrumbs={[
        {
          title: kind === 'revision' ? 'Revízne správy' : 'Formuláre',
          link: kind === 'revision' ? REVISIONS_PAGE_PATH.original : FORMS_PAGE_PATH.original,
        },
        { title: kind === 'revision' ? 'Vytvorenie revíznej správy' : 'Vytvorenie formulára' },
      ]}
      title={kind === 'revision' ? 'Vytvorenie revíznej správy' : 'Vytvorenie formulára'}
    >
      {!!initialValues && (
        <RevisionForm
          context="create"
          kind={kind}
          onSubmit={onSubmit}
          initialValues={initialValues}
          primaryButtonText="Vytvoriť"
          primaryIcon={<IconPlus stroke={1.5} size={24} />}
          readOnly={{
            organizationId: !!searchParams.get('organizationId'),
            departmentId: !!searchParams.get('departmentId'),
            deviceTypeId: !!searchParams.get('deviceTypeId'),
          }}
        />
      )}
    </DashboardLayout>
  );
}
